import { useEffect, useState } from "react";

/**
 * Lightweight preflight that pings youtube.com once on mount and reports
 * whether the network/browser is willing to talk to YouTube at all.
 *
 * Uses a no-cors HEAD-equivalent fetch against the public oEmbed endpoint
 * for the YouTube homepage logo video — any successful round-trip (even an
 * opaque response) means the network is reachable. A rejected promise
 * means DNS failure, CSP block, captive portal, ad-blocker, or offline.
 *
 * Returns `null` while checking, `true` if reachable, `false` if blocked.
 */
export function useYouTubeReachable(timeoutMs = 4000): boolean | null {
  const [reachable, setReachable] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);

    fetch("https://www.youtube.com/favicon.ico", {
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    })
      .then(() => { if (!cancelled) setReachable(true); })
      .catch(() => { if (!cancelled) setReachable(false); })
      .finally(() => window.clearTimeout(timer));

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [timeoutMs]);

  return reachable;
}